<template>
  <div
    class="pathfinder-gl"
    ref="scene"
  >
  </div>
</template>

<script>
  /**
   * OUTLINE
   *
   * * Load Base Scene
   * * Load Textures
   * * Load Model w/ Animations
   * * Change select material (materials[0] or material) to custom material
   * * Setup up state component for changeable materials to vue state syncing
   *
   * MAYBE DO
   * ? Saving/Loading of node material
   * ? Sync changes on change not on update
   */
  import { mapState, mapActions } from "vuex";

  import * as THREE from "three";
  import "three/examples/js/loaders/GLTFLoader";
  import "three/examples/js/loaders/DRACOLoader";
  import "three/examples/js/controls/OrbitControls";
  import "three/examples/js/nodes/THREE.Nodes";
  import "three/examples/js/loaders/NodeMaterialLoader";

  import CustomizeableMaterial from "@/components/Pathfinder/WebGlView/js/customizeable-material";
  import ItemManager from "@/components/Pathfinder/WebGlView/js/item-manager";

  const jsonLoader = new THREE.ObjectLoader();
  const gltfLoader = new THREE.GLTFLoader();
  THREE.DRACOLoader.setDecoderPath("draco/");
  THREE.DRACOLoader.setDecoderConfig({ type: "js" }); // (Optional) Override detection of WASM support.
  gltfLoader.setDRACOLoader(new THREE.DRACOLoader());

  let renderer, scene, camera;

  // TODO setup global class that handles the scene, animation mixer, etc. and loading/unloading models
  // TODO load all models on startup and cache?
  // TODO Remove duplicate event handlers

  export default {
    name: "WebGlView",
    data() {
      return {
        debug: false
      };
    },
    computed: {
      ...mapState({
        pathfinder: state => state.pathfinders.pathfinder,
        types: state => state.types.types,
        patterns: state => state.patterns.patterns,
        items: state => state.items.items
      }),
      type() {
        return this.types.find(t => t.key === this.pathfinder.type);
      },
      height() {
        return this.$refs.scene.clientHeight;
      },
      width() {
        return this.$refs.scene.clientWidth;
      }
    },
    methods: {
      ...mapActions(["getModelForType"]),
      resize() {
        const width = this.$refs.scene.clientWidth;
        const height = this.$refs.scene.clientHeight;
        const aspect = width / height;
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      },
      captureAvatar(w, h) {
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();

        renderer.render(scene, camera, null, false);
        const avatar = renderer.domElement.toDataURL();

        renderer.setSize(this.width, this.height);
        camera.aspect = this.width / this.height;
        camera.updateProjectionMatrix();

        return avatar;
      },
      setupDevControls() {
        controls = new THREE.OrbitControls(camera, renderer.domElement);
        // controls.maxDistance = modelSize * 50;
        controls.enableDamping = true;
        controls.dampingFactor = 0.07;
        controls.rotateSpeed = 0.07;
        controls.panSpeed = 0.07;
        controls.screenSpacePanning = true;
      },
      positionCameraOffOfObject(object) {
        const y = 0.25;
        camera.position.set(0, 0, 0);
        camera.position.add(new THREE.Vector3(-0.75, y, 1.35));
        const lookAt = new THREE.Vector3()
          .add(object.position)
          .add(new THREE.Vector3(0, y, 0));
        camera.lookAt(lookAt);
      },
      setup() {
        let mixer, controls, customMaterial, itemManager;

        // LOADING
        const loadingComponent = this.$loading.open({
          container: this.$refs.scene
        });

        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true
        });
        renderer.physicallyCorrectLights = false;
        renderer.gammaOutput = false;
        renderer.gammaFactor = 2.2;
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(this.width, this.height);
        this.$refs.scene.appendChild(renderer.domElement);

        camera = new THREE.PerspectiveCamera(
          40,
          this.width / this.height,
          0.1,
          1000
        );

        if (this.debug) this.setupDevControls();

        jsonLoader.load(
          // resource URL
          // ? Can be glb to save space
          "scenes/stump_scene.json",

          loaded => {
            scene = loaded;
            scene.background = null;
            // Add more lighting
            const light = new THREE.AmbientLight(0x404040); // soft white light
            scene.add(light);
            const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
            scene.add(directionalLight);

            this.getModelForType(this.type.id).then(gltf => {
              const model = gltf.scene.children[0];
              let root = scene.children.find(
                c => c.name === this.type.scene_node
              );
              root = root ? root : scene;

              this.positionCameraOffOfObject(root);

              itemManager = new ItemManager();
              customMaterial = new CustomizeableMaterial(
                this.pathfinder,
                this.$textures
              );

              itemManager.init(model, this.items);

              model.traverse(child => {
                if (child.isMesh & !itemManager.hasChild(child)) {
                  customMaterial.convertMaterial(child);
                }
              });

              mixer = new THREE.AnimationMixer(model);
              const action = mixer.clipAction(gltf.animations[0]);
              action.play();

              root.add(model);

              // END LOADING
              loadingComponent.close();
            });
          },

          // onProgress callback
          function(xhr) {
            console.log("Scene " + (xhr.loaded / xhr.total) * 100 + "% loaded");
          },

          // onError callback
          function(err) {
            console.error("An error happened");
          }
        );

        const clock = new THREE.Clock();
        let delta;
        const animate = () => {
          requestAnimationFrame(animate);
          delta = clock.getDelta();
          if (mixer) mixer.update(delta);
          if (controls) controls.update();
          if (itemManager) itemManager.update(this.pathfinder);
          if (customMaterial) customMaterial.update(this.pathfinder);
          if (scene) renderer.render(scene, camera);
        };

        animate();
      },
      teardown() {
        renderer.forceContextLoss();
        renderer.context = null;
        renderer.domElement = null;
        renderer = null;
        scene = null;
        camera = null;
      }
    },
    mounted() {
      window.addEventListener("resize", this.resize, false);
      this.setup();
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.resize, false);
      this.teardown();
    }
  };
</script>
